import { z } from 'zod';

export const ENERGY_LABELS = [
  'Unknown',
  'A++++',
  'A+++',
  'A++',
  'A+',
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
] as const;

export const CalculateMaximumMortgageFormSchema = z
  .object({
    income: z
      .number({ message: 'Vul je inkomen in' })
      .min(1, 'Vul een geldig inkomen in')
      .max(10000000, 'Inkomen lijkt onrealistisch hoog'),

    age: z
      .number({ message: 'Vul je leeftijd in' })
      .int('Leeftijd moet een heel getal zijn')
      .min(18, 'Je moet minimaal 18 jaar oud zijn')
      .max(100, 'Leeftijd moet 100 jaar of jonger zijn'),

    hasPartner: z.boolean(),

    partnerIncome: z
      .number({ message: 'Vul het inkomen van je partner in' })
      .min(1, 'Vul een geldig inkomen in')
      .max(10000000, 'Inkomen lijkt onrealistisch hoog')
      .optional(),

    partnerAge: z
      .number({ message: 'Vul de leeftijd van je partner in' })
      .int('Leeftijd moet een heel getal zijn')
      .min(18, 'Partner moet minimaal 18 jaar oud zijn')
      .max(100, 'Leeftijd moet 100 jaar of jonger zijn')
      .optional(),

    hasObligations: z.boolean(),

    loans: z
      .number({ message: 'Vul het maandelijkse bedrag aan leningen in' })
      .min(0, 'Bedrag kan niet negatief zijn')
      .max(100000, 'Bedrag lijkt onrealistisch hoog')
      .optional(),

    studentDebt: z
      .number({ message: 'Vul het maandelijkse bedrag aan studieleningen in' })
      .min(0, 'Bedrag kan niet negatief zijn')
      .max(100000, 'Bedrag lijkt onrealistisch hoog')
      .optional(),

    alimony: z
      .number({ message: 'Vul het maandelijkse bedrag aan alimentatie in' })
      .min(0, 'Bedrag kan niet negatief zijn')
      .max(100000, 'Bedrag lijkt onrealistisch hoog')
      .optional(),

    mortgageType: z.enum(['Annuity', 'Linear', 'InterestOnly']),

    interestPeriod: z.enum(['5', '10', '15', '20', '30']),

    useNhg: z.boolean(),

    energyLabel: z.enum(ENERGY_LABELS),
  })
  .superRefine((data, ctx) => {
    if (data.hasPartner) {
      if (
        data.partnerIncome === undefined ||
        Number.isNaN(data.partnerIncome)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Vul het inkomen van je partner in',
          path: ['partnerIncome'],
        });
      }
      if (data.partnerAge === undefined || Number.isNaN(data.partnerAge)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Vul de leeftijd van je partner in',
          path: ['partnerAge'],
        });
      }
    }

    if (data.hasObligations) {
      const hasAnyObligation =
        (data.loans && data.loans > 0) ||
        (data.studentDebt && data.studentDebt > 0) ||
        (data.alimony && data.alimony > 0);

      if (!hasAnyObligation) {
        ctx.addIssue({
          code: 'custom',
          message:
            'Vul minimaal één verplichting in (lening, studieschuld of alimentatie)',
          path: ['hasObligations'],
        });
      }
    }
  });

export const SearchSchema = z.object({
  search: z.string().min(2, 'Vul een geldige postcode of plaats in'),
  searchScope: z.enum([
    'aankoopmakelaar',
    'aansprakelijkheidsverzekering',
    'arbeidsongeschiktheidsverzekering',
    'bankzaken',
    'bedrijfsschadeverzekering',
    'belasting',
    'betalingsrisicoverzekering',
    'duurzaamwonen',
    'financieleplanning',
    'hypotheek',
    'lenen',
    'notariaat',
    'pensioen',
    'pensioenregeling',
    'rechtsbijstandverzekering',
    'scheiding',
    'taxatie',
    'uitvaartverzorging',
    'verkoopmakelaar',
    'vermogen',
    'verzekering',
    'verzuimverzekering',
    'zakelijke-financiering',
  ]),
});

export type CalculateMaximumMortgageFormValues = z.infer<
  typeof CalculateMaximumMortgageFormSchema
> & {
  search?: string;
};
export type SearchFormValues = z.infer<typeof SearchSchema>;
